import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { format, FORMATS } from "utils/datetime";
import { reverse } from "utils/urls";

import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Select from "components/Select";
import Stack from "components/Stack";
import { usePageTitle } from "components/usePageTitle";

import AssetManagementTable from "entities/AssetManagement/components/AssetManagementTable";
import AssetManagementTabs from "entities/AssetManagement/components/AssetManagementTabs";
import { extractRowKey } from "entities/AssetManagement/components/Table/utils";
import { AMColumn, AMResolution } from "entities/AssetManagement/constants";
import {
  IAssetManagementCategory,
  IAssetManagementRows,
  useAssetManagementCategories,
  useAssetManagementData,
  useVersions,
  useVersionsDiff,
} from "entities/AssetManagement/sdk";

const AssetManagementVersions = () => {
  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);
  usePageTitle("Builders Patch: Asset Management Version History");

  const [selectedVersion, setSelectedVersion] = useState<number>();
  const [selectedTab, setSelectedTab] =
    useState<IAssetManagementCategory | null>(null);
  const [resolution, setResolution] = useState<AMResolution>(
    AMResolution.YEARLY
  );

  const { data: versions } = useVersions({ dealId });
  const { data: tabs } = useAssetManagementCategories({ dealId });

  const { data: diff, isLoading: isLoadingDiff } = useVersionsDiff({
    prevVersionId: selectedVersion,
    newVersionId: _.first(versions)?.id,
  });

  useEffect(() => setSelectedVersion(_.first(versions)?.id), [versions]);
  useEffect(() => {
    if (!_.isNil(tabs) && _.isNil(selectedTab)) {
      setSelectedTab(tabs[0]);
    }
  }, [tabs, selectedTab]);

  const { data: newData } = useAssetManagementData({
    dealId,
  });

  const allDataRows = useMemo(() => {
    if (_.isNil(newData)) return { rows: [] };

    const rows = newData.categories.flatMap((category) =>
      category.sub_categories.flatMap((item) =>
        item.rows.map((row) => ({
          ...row,
          sub_category: {
            id: item.id,
            name: item.name,
            category: { id: category.id, name: category.name },
          },
          is_calculation_item: false,
          is_total_row: false,
        }))
      )
    );

    return { rows };
  }, [newData]) as IAssetManagementRows;

  const deletedRows = Object.keys(diff?.rows_diff || {})
    .filter((rowKey) => diff?.rows_diff[rowKey].is_deleted)
    .map((rowKey) => {
      const { itemCode, categoryId, subCategoryName } = extractRowKey({
        itemKey: rowKey,
      });
      return {
        code: itemCode,
        categoryId,
        subCategory: subCategoryName,
        name: diff?.rows_diff[rowKey].previous_label || "",
        is_deleted: true,
        date_values: [],
      };
    });

  const data = useMemo(
    () =>
      newData
        ? {
            data: {
              ...(tabs || []).reduce(
                (acc, tab) => ({
                  ...acc,
                  [tab.name]: [],
                }),
                {}
              ),
              ..._.mapValues(
                _.groupBy(
                  allDataRows.rows || [],
                  (row) => row.sub_category.category.name
                ),
                (value) => {
                  return _.map(
                    _.groupBy(
                      value,
                      (row) =>
                        `${row.sub_category.category.id}|${row.sub_category.name || ""}`
                    ),
                    (valueRow, compositeKey) => ({
                      data_points: [
                        ...valueRow.map((val) => ({
                          code: val.item_code,
                          name: val.name,
                          date_values: _.map(
                            _.groupBy(
                              val.values,
                              (item) =>
                                `${item.date_info.year}|${
                                  item.date_info.quarter || ""
                                }|${item.date_info.month || ""}`
                            ),
                            (dataValuesList, dateInfoKey) => {
                              const [yearKey, quarterKey, monthKey] =
                                dateInfoKey.split("|");

                              return {
                                ...dataValuesList.reduce(
                                  (prev, curr) => ({
                                    ...prev,
                                    [curr.data_type]: {
                                      value: curr.value,
                                      id: null,
                                      aggregated: true,
                                    },
                                  }),
                                  {}
                                ),
                                date_info: {
                                  year: Number(yearKey),
                                  quarter: quarterKey || null,
                                  month: monthKey || null,
                                },
                              };
                            }
                          ),
                        })),
                        ...deletedRows.filter(
                          (row) =>
                            `${row.categoryId}|${row.subCategory}` ===
                            compositeKey
                        ),
                      ],
                      key: compositeKey,
                      total_values: (() => {
                        const myrow = valueRow.map((row) => ({
                          ...row,
                          valuesObj: row.values.reduce<{ [key: string]: any }>(
                            (prev, curr) => ({
                              ...prev,
                              [`${curr.date_info.year}/${curr.date_info.quarter}/${curr.date_info.month}/${curr.data_type}`]:
                                curr,
                            }),
                            {}
                          ),
                        }));

                        const totalRow = myrow.reduce((prev, current) => {
                          const keys = Array.from(
                            new Set([
                              ...Object.keys(prev.valuesObj),
                              ...Object.keys(current.valuesObj),
                            ])
                          );

                          return {
                            ...prev,
                            valuesObj: keys.reduce((prevObj, currentKey) => {
                              return {
                                ...prevObj,
                                [currentKey]: {
                                  ..._.get(
                                    prevObj,
                                    currentKey,
                                    _.get(current, `valuesObj.${currentKey}`)
                                  ),
                                  value:
                                    Number(
                                      _.get(prevObj, `${currentKey}.value`, 0)
                                    ) +
                                    Number(
                                      _.get(
                                        current,
                                        `valuesObj.${currentKey}.value`,
                                        0
                                      )
                                    ),
                                },
                              };
                            }, prev.valuesObj),
                          };
                        });

                        return _.map(
                          _.groupBy(
                            totalRow.valuesObj,
                            (item) =>
                              `${item.date_info.year}|${
                                item.date_info.quarter || ""
                              }|${item.date_info.month || ""}`
                          ),
                          (dataValuesList, dateInfoKey) => {
                            const [yearKey, quarterKey, monthKey] =
                              dateInfoKey.split("|");

                            return {
                              ...dataValuesList.reduce(
                                (prev, curr) => ({
                                  ...prev,
                                  [curr.data_type]: {
                                    value: curr.value,
                                    id: null,
                                    aggregated: true,
                                  },
                                }),
                                {}
                              ),
                              date_info: {
                                year: Number(yearKey),
                                quarter: quarterKey || null,
                                month: monthKey || null,
                              },
                            };
                          }
                        );
                      })(),
                    })
                  );
                }
              ),
            },
            headers: newData.headers.filter((header) => {
              if (resolution === AMResolution.QUARTERLY) {
                return !_.isNull(header.quarter) && _.isNull(header.month);
              }

              if (resolution === AMResolution.MONTHLY) {
                return !_.isNull(header.month) && _.isNull(header.quarter);
              }

              return _.isNull(header.month) && _.isNull(header.quarter);
            }),
          }
        : undefined,
    [deletedRows, resolution, tabs, allDataRows, newData]
  );

  const versionSelectOptions = useMemo(
    () =>
      versions?.map((version) => ({
        value: version.id.toString(),
        label: `Version ${version.version_number} created by ${
          version.created_by.first_name
        } ${version.created_by.last_name} on ${format(
          new Date(version.created_at),
          FORMATS.FRONTEND.DATETIME
        )}`,
      })) || [],
    [versions]
  );

  return (
    <Paper
      sx={{
        padding: theme.spacing(0, 4, 1, 4),
      }}
    >
      <Stack spacing={3}>
        <PageHeader
          backLink={reverse(URLS.DEAL_ASSET_MANAGEMENT, { dealId })}
          backTitle="Back to Asset Management"
          title="Asset management version history"
          actions={
            <Stack direction="row" spacing={2}>
              <Select
                size="small"
                sx={{ width: "300px" }}
                inputProps={{
                  sx: {
                    backgroundColor: "white",
                  },
                }}
                helperText="Show yearly, quarterly or monthly data."
                onChange={(e) => setResolution(e.target.value as AMResolution)}
                label="Time Period"
                options={Object.values(AMResolution).map((item) => ({
                  label: item,
                  value: item,
                }))}
                value={resolution}
                data-testid="time-period-dropdown"
                dropdownOptionsDataTestid="time-period-dropdown-option"
              />
              <Select
                size="small"
                value={selectedVersion?.toString() || ""}
                sx={{ width: "400px" }}
                inputProps={{
                  sx: {
                    backgroundColor: "white",
                  },
                }}
                label="Version"
                helperText="Pick a version you would like to compare with the current version"
                options={versionSelectOptions}
                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                data-testid="am-versions-dropdown"
                dropdownOptionsDataTestid="am-versions-dropdown-option"
              />
            </Stack>
          }
        />
        {!_.isNil(selectedTab) && !_.isNil(tabs) && (
          <AssetManagementTabs
            tabs={tabs}
            selectedTab={selectedTab}
            onChange={(tab) => setSelectedTab(tab)}
          />
        )}

        {!_.isNil(selectedTab) && data && (
          <AssetManagementTable
            varianceDisplayMode={[]}
            rowDelete={() => {}}
            itemDelete={() => {}}
            selectedTab={selectedTab}
            columns={
              resolution === AMResolution.MONTHLY
                ? [AMColumn.BUDGETED, AMColumn.ACTUAL]
                : [AMColumn.BUDGETED, AMColumn.ACTUAL, AMColumn.AUDITED]
            }
            showSubItems={true}
            loading={isLoadingDiff}
            data={data}
            editMode={false}
            resolution={resolution}
            diff={diff}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default AssetManagementVersions;
